import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Card as MuiCard,
  InputBase,
  Button,
  IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getEnrolledChannels, getProgramById } from "../../redux/actions/programAction";
import { getUserProgramEnrollmentData } from "../../redux/actions/programEnrollmentAction";
import useStyles from "../../containers/Auth/Onboarding/style";
import appLogo from "../../assets/logo.png";
import backIcon from "../../assets/icons/backIcon.svg";
import { RootState } from "../../redux/store";
import { Search, Edit, DragIndicator, Delete } from "@material-ui/icons";
import { useSearchChannels } from "../../util/hooks";
import {
  DragDropContext,
  Droppable,
  Draggable,
} from "react-beautiful-dnd";
import { assetUrl } from "../../util/assetUrl";
import { INSTANCE } from "../../api/endpoint"; // ← NEW
import AddChannelIcon from "../../components/Icons/AddChannelIcon"; // ← NEW
import { usePrivileges } from "../../util/usePrivileges";
import NotificationPolicyEditor from "../../components/NotificationPolicyEditor";
import AdminChatroomManager from "./AdminChatroomManager";
import AdminEmailSettings from "./AdminEmailSettings";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
// 🔧 Use ASSET base when serving stored files (/assets, /uploads)
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;

// 🔧 Resolve stored paths using ASSET_BASE (supports blob/data/absolute/imports too)
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

// THEME: readable text for secondaryColor
const invertColor = (hex: string) => {
  let col = (hex || "#ffffff").replace(/^#/, "");
  if (col.length === 3) col = col.split("").map(c => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

// ---- NEW: simple slug helper for defaulting Channel ID from Name (VERBATIM)
const slugify = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function AdminChannelManagment() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [view, setView] = useState<"dashboard" | "channels" | "notificationPolicy" | "chatrooms" | "emailSettings">("dashboard");
  const [mode, setMode] = useState<"edit" | "order">("edit");
  const [orderedChannels, setOrderedChannels] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  // 🔧 cache-bust for community logo when it changes
  const [logoBust, setLogoBust] = useState<number>(0);

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false }, []);

  const params = new URLSearchParams(location.search);
  const initialCommunity = params.get("community") || undefined;
  const [selectedCommunity, setSelectedCommunity] = useState<string | undefined>(initialCommunity);

  const { singleProgram } = useSelector((s: RootState) => s.programReducer);
  const { user } = useSelector((s: RootState) => s.auth);
  const { enrollment } = useSelector((s: RootState) => s.programEnrollmentReducer);
  const activeCommunity = singleProgram?.communities.find(
    (cm) => selectedCommunity && cm.communityIdentifier === selectedCommunity
  );
  const channels = activeCommunity?.channels ?? [];

  // THEME: pull community colors once (NOW INCLUDES headerImage)
  const communityTheme = {
    mainColor: activeCommunity?.communityStyle?.mainColor || "#1976d2",
    secondaryColor: activeCommunity?.communityStyle?.secondaryColor || "#ffffff",
    fontStyle: activeCommunity?.communityStyle?.fontStyle || "Arial",
    headerImage: activeCommunity?.communityStyle?.headerImage || "",   // ← NEW
    // logo path lives here:
    logo: activeCommunity?.communityStyle?.logo || "",
  };
  const fgOnSecondary = invertColor(communityTheme.secondaryColor);

  // 🔧 bump bust any time the community/logo changes
  useEffect(() => {
    setLogoBust(Date.now());
  }, [activeCommunity?.communityIdentifier, activeCommunity?.communityStyle?.logo]);

  // 🔧 compute resolved logo URL with cache-busting; fallback to app logo
  const communityLogoUrl = useMemo(() => {
    const raw = communityTheme.logo;
    const resolved = resolveHeaderUrl(raw);
    if (!resolved) return "";
    return logoBust ? `${resolved}${resolved.includes("?") ? "&" : "?"}v=${logoBust}` : resolved;
  }, [communityTheme.logo, logoBust]);

  // Update ordered channels when community changes or channels are populated
  useEffect(() => {
    setOrderedChannels(activeCommunity?.channels || []);
  }, [selectedCommunity, activeCommunity]);

  useEffect(() => {
    if (!singleProgram?._id || !user?._id) return;
    const matches =
      enrollment &&
      enrollment.programId &&
      String(enrollment.programId) === String(singleProgram._id) &&
      enrollment.userId &&
      String(enrollment.userId) === String(user._id);
    if (!matches) {
      dispatch(getUserProgramEnrollmentData(singleProgram._id, user._id));
    }
  }, [dispatch, enrollment, singleProgram?._id, user?._id]);

  const {
    privileges,
    fallbackPlatformAdmin,
    fallbackCommunityAdmin,
    communityRole,
    communityModeratedChannels,
  } = usePrivileges(selectedCommunity || undefined);

  const normalizedUserType = (user?.type || '').trim().toLowerCase();
  const isTeamAdmin = normalizedUserType === 'team-admin';

  const normalizedCommunityRole = (communityRole || '').toString().trim().toLowerCase();
  const elevatedOverrides = useMemo(
    () =>
      communityModeratedChannels.filter((channel) => {
        const role = (channel.role || 'moderator').toString().trim().toLowerCase();
        return role === 'admin' || role === 'moderator';
      }),
    [communityModeratedChannels]
  );
  const hasChannelOverrides = elevatedOverrides.length > 0;
  const isModeratorRole = normalizedCommunityRole === 'moderator' || hasChannelOverrides;
  const isCommunityAdminRole =
    normalizedCommunityRole === 'communityadmin' ||
    privileges.isCommunityAdmin(selectedCommunity || undefined) ||
    fallbackCommunityAdmin;
  const isPlatformAdmin = privileges.platformAdmin || fallbackPlatformAdmin;

  const canManageCommunityWide = isPlatformAdmin || isCommunityAdminRole || isTeamAdmin;
  const canModerateOnly = !canManageCommunityWide && isModeratorRole;
  const hasAccess = canManageCommunityWide || canModerateOnly;

  const canAccessRoles = canManageCommunityWide && (isPlatformAdmin || isCommunityAdminRole || isTeamAdmin);

  const enrollmentLoaded =
    privileges.activeEnrollments.length > 0 || fallbackPlatformAdmin || fallbackCommunityAdmin || isTeamAdmin;

  const displayedChannels = useMemo(() => {
    if (!Array.isArray(orderedChannels)) return [];
    if (!selectedCommunity) return orderedChannels;

    return orderedChannels.filter((channel: any) => {
      const identifier = (channel && channel.channelIdentifier) || '';
      if (!identifier) return false;

      if (!isPlatformAdmin && !isTeamAdmin) {
        if (!privileges.canViewChannel(selectedCommunity, identifier)) return false;
      }

      if (isPlatformAdmin || isTeamAdmin) return true;
      return privileges.canEditChannel(selectedCommunity, identifier);
    });
  }, [orderedChannels, privileges, selectedCommunity, isPlatformAdmin, isTeamAdmin]);

  const { setSearchQuery, filteredResults } = useSearchChannels(
    mode === "edit" ? displayedChannels : []
  );

  useEffect(() => {
    if (!canManageCommunityWide && mode === "order") {
      setMode("edit");
    }
  }, [canManageCommunityWide, mode]);

  useEffect(() => {
    if (singleProgram && !selectedCommunity) {
      const fallback = singleProgram.communities?.[0]?.communityIdentifier;
      if (fallback) {
        setSelectedCommunity(fallback);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('community', fallback);
        history.replace({ search: searchParams.toString() });
      }
    }
  }, [singleProgram, selectedCommunity, history, location.search]);

  useEffect(() => {
    const nextCommunity = new URLSearchParams(location.search).get('community') || undefined;
    if (nextCommunity && nextCommunity !== selectedCommunity) {
      setSelectedCommunity(nextCommunity);
    }
  }, [location.search, selectedCommunity]);

  // 🔧 NEW: Fetch program data on mount if not already loaded (fixes reload bug)
  useEffect(() => {
    const activeProgramEnrollment = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
    const programId = activeProgramEnrollment?.programId;

    // If we don't have singleProgram in Redux yet, fetch it
    if (programId && !singleProgram) {
      dispatch(getProgramById(programId) as any);
    }
  }, [dispatch, singleProgram]);

  useEffect(() => {
    if (activeCommunity && singleProgram && selectedCommunity) {
      const { _id: programId } = singleProgram;
      const userId = JSON.parse(localStorage.getItem("authInfo") || "{}")._id;
      dispatch(getEnrolledChannels(programId, userId, selectedCommunity));
    }
  }, [activeCommunity, selectedCommunity, singleProgram, dispatch]);

  const onDragEnd = useCallback((result) => {
    if (!canManageCommunityWide) return;
    if (!result.destination) return;
    const updated = Array.from(orderedChannels);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    setOrderedChannels(updated);
  }, [orderedChannels, canManageCommunityWide]);

  const persistProgram = async (updatedProgram: any) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/programs/${singleProgram!._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedProgram),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (err) {
      console.error("[Program Persist Error]", err);
    }
  };

  const handleSaveOrder = () => {
    if (!singleProgram || !canManageCommunityWide) return;
    setSaving(true);

    const updatedProgram = {
      ...singleProgram,
      communities: singleProgram.communities.map((c) =>
        c.communityIdentifier === selectedCommunity
          ? { ...c, channels: orderedChannels }
          : c
      ),
    };

    // optimistic update
    dispatch({ type: "GET_SINGLE_PROGRAM", payload: updatedProgram });
    dispatch({ type: "CLEAR_SUBSCRIBE_CHANNELS" });

    (async () => {
      try {
        await persistProgram(updatedProgram);
        // Return to dashboard view after saving
        if (isMounted.current) {
          setView("dashboard");
          setMode("edit");
        }
      } finally {
        if (isMounted.current) setSaving(false);
      }
    })();
  };

  const handleDeleteChannel = (ch: any) => {
    if (!singleProgram || !canManageCommunityWide) return;
    if (!window.confirm(`Delete channel "${ch.channelName}"? This cannot be undone.`)) return;

    setSaving(true);

    const filtered = orderedChannels.filter(
      (c) => c.channelIdentifier !== ch.channelIdentifier
    );
    setOrderedChannels(filtered);

    const updatedProgram = {
      ...singleProgram,
      communities: singleProgram.communities.map((c) =>
        c.communityIdentifier === selectedCommunity
          ? { ...c, channels: filtered }
          : c
      ),
    };

    // optimistic update
    dispatch({ type: "GET_SINGLE_PROGRAM", payload: updatedProgram });
    dispatch({ type: "CLEAR_SUBSCRIBE_CHANNELS" });

    (async () => {
      try {
        await persistProgram(updatedProgram);
      } finally {
        if (isMounted.current) setSaving(false);
      }
    })();
  };

  const handleEdit = (ch: any) => {
    history.push(
      `/admin/programs/${singleProgram!._id}/communities/${selectedCommunity}/channels/${ch.channelIdentifier}`,
      { channel: ch, communityIdentifier: selectedCommunity }
    );
  };

  /* =========================
     ADD CHANNEL (VERBATIM)
     ========================= */
  // —— NEW: Add Channel modal state
  const [addOpen, setAddOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelId, setNewChannelId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string>("");

  // —— NEW: construct a default channel object (theme-aware) — same as sidebar
  const buildNewChannel = (name: string, identifier: string, channelStyle?: any) => ({
    channelPrivacySettings: { type: "restricted" },
    channelPostingSettings: {
      icons: ["note"],
      defaultIcon: "note",
      canComment: ["admin", "moderator"],
      canPost: ["admin", "moderator"],
      canEditPosts: ["admin", "moderator"],
    },
    channelData: { isDefaultChannel: false },
    publish: true,
    about: "",
    channelFormSettings: { forms: [] },
    channelDetails: { description: "" },
    channelStyle: channelStyle || {
      mainColor: communityTheme.mainColor,
      secondaryColor: communityTheme.secondaryColor,
      headerImage: communityTheme.headerImage, // ← CHANGED: use the SAME header image as community
      fontStyle: communityTheme.fontStyle,
    },
    channelType: "Posts",
    channelName: name,
    channelIdentifier: identifier,
  });

  // —— NEW: handle create flow — mirrors sidebar version
  const handleCreateChannel = async () => {
    if (!canManageCommunityWide) return;
    try {
      setCreateErr("");
      if (!singleProgram || !singleProgram._id || !selectedCommunity) {
        setCreateErr("Missing program context.");
        return;
      }

      const name = (newChannelName || "").trim();
      const idInput = (newChannelId || "").trim();

      if (!name) {
        setCreateErr("Please enter a channel name.");
        return;
      }

      const finalId = idInput ? slugify(idInput) : slugify(name);
      if (!finalId) {
        setCreateErr("Channel ID cannot be empty.");
        return;
      }

      const community = singleProgram.communities?.find(
        (c: any) => c?.communityIdentifier === selectedCommunity
      );
      const existingChannels: any[] = community?.channels || [];

      const nameExists = existingChannels.some(
        (ch) => ((ch?.channelName || "").trim().toLowerCase() === name.toLowerCase())
      );
      if (nameExists) {
        setCreateErr("A channel with this name already exists in this community.");
        return;
      }

      const idExists = existingChannels.some(
        (ch) => ((ch?.channelIdentifier || "").trim().toLowerCase() === finalId.toLowerCase())
      );
      if (idExists) {
        setCreateErr("A channel with this ID already exists in this community.");
        return;
      }

      setCreating(true);

      const newChannel = buildNewChannel(name, finalId, communityTheme);

      const updatedProgram = {
        ...(singleProgram as any),
        communities: (singleProgram.communities || []).map((c: any) =>
          c.communityIdentifier === selectedCommunity
            ? { ...c, channels: [...(c.channels || []), newChannel] }
            : c
        ),
      };

      // optimistic UI
      dispatch({ type: "GET_SINGLE_PROGRAM", payload: updatedProgram });
      dispatch({ type: "CLEAR_SUBSCRIBE_CHANNELS" });

      try {
        await INSTANCE.put(`/v1/programs/${singleProgram._id}`, updatedProgram);
      } catch (e) {
        const err = e as any;
        const status = err?.response?.status;
        const backendMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message;
        if (status === 409 || /duplicate/i.test(backendMsg || "")) {
          setCreateErr("Duplicate channel name or ID. Please choose a different one.");
        } else {
          setCreateErr(backendMsg || "Failed to create channel.");
        }
        setCreating(false);
        return;
      }

      setAddOpen(false);
      setNewChannelName("");
      setNewChannelId("");

      // navigate to the channel editor (use the AdminChannelManagment convention)
      history.push(`/admin/programs/${singleProgram._id}/communities/${selectedCommunity}/channels/${newChannel.channelIdentifier}`);
    } catch (e) {
      const err = e as any;
      const msg = err?.response?.data?.message || err?.message || "Failed to create channel.";
      setCreateErr(msg);
    } finally {
      setCreating(false);
    }
  };

  if (!selectedCommunity || !activeCommunity) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    if (!enrollmentLoaded) {
      return (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <MuiCard className={classes.card} style={{ padding: 24, margin: 24 }}>
        <Typography variant="h6" gutterBottom>
          Access Restricted
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You do not have permission to manage channels for this community.
        </Typography>
      </MuiCard>
    );
  }

  return (
  <div
    className={classes.container}
    style={{
      backgroundColor: communityTheme.secondaryColor,
      minHeight: "100vh",
      padding: "20px",
      display: "flex",
      justifyContent: "center",
    }}
  >
    {/* ===== Add Channel Modal ===== */}
    {canManageCommunityWide && (
    <Dialog
      open={addOpen}
      onClose={() => !creating && setAddOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          backgroundColor: communityTheme.secondaryColor,
          color: fgOnSecondary,
          fontFamily: communityTheme.fontStyle,
        },
      }}
    >
      <DialogTitle style={{ color: fgOnSecondary }}>Add Channel</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Channel Name"
          fullWidth
          value={newChannelName}
          onChange={(e) => setNewChannelName(e.target.value)}
          InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
          InputProps={{ style: { color: fgOnSecondary } }}
        />
        <TextField
          margin="dense"
          label="Channel ID (optional — defaults to slug of name)"
          fullWidth
          value={newChannelId}
          onChange={(e) => setNewChannelId(e.target.value)}
          helperText="Lowercase, hyphen-separated. If left blank, it will use a slug of the Channel Name."
          FormHelperTextProps={{ style: { color: `${fgOnSecondary}99` } }}
          InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
          InputProps={{ style: { color: fgOnSecondary } }}
        />
        {createErr ? (
          <div style={{ color: "#d32f2f", marginTop: 8 }}>{createErr}</div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddOpen(false)} disabled={creating} style={{ color: fgOnSecondary }}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCreateChannel}
          disabled={creating}
          style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
        >
          {creating ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
    )}

    <MuiCard
      className={classes.card}
      style={{
        height: "95vh",
        width: "100%",
        maxWidth: "1600px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: communityTheme.secondaryColor,
        color: fgOnSecondary,
        border: `1px solid ${fgOnSecondary}33`,
        boxShadow: `0 8px 24px ${fgOnSecondary}22`,
        fontFamily: communityTheme.fontStyle,
        overflow: "hidden",
      }}
    >
        {/* Header */}
        <Box
          className={classes.header}
          style={{
            flexShrink: 0,
            backgroundColor: communityTheme.mainColor,
            color: communityTheme.secondaryColor,
          }}
        >
          <IconButton onClick={() => history.push("/user/dashboard")}>
            <img src={backIcon} alt="Back" className={classes.backIcon} />
          </IconButton>
          <Box className={classes.logoWrapper}>
            <img
              src={communityLogoUrl || resolveHeaderUrl(appLogo)}
              alt="Logo"
              className={classes.logo}
            />
          </Box>
        </Box>

        {/* Title Section */}
        <Box style={{ flexShrink: 0, padding: "16px 16px 0 16px" }}>
          {view === "dashboard" ? (
            <>
              <Typography
                className={`${classes.titleCon} ${classes.onboardingTitle}`}
                component="h1"
                style={{ color: communityTheme.mainColor, marginBottom: 4 }}
              >
                Community Dashboard
              </Typography>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}99`, marginBottom: 16 }}
              >
                {selectedCommunity}
              </Typography>
            </>
          ) : view === "notificationPolicy" ? (
            <>
              <Box display="flex" alignItems="center" mb={1}>
                <IconButton
                  size="small"
                  onClick={() => setView("dashboard")}
                  style={{ marginRight: 8, color: communityTheme.mainColor }}
                >
                  <i className="material-icons">arrow_back</i>
                </IconButton>
                <Typography
                  className={`${classes.titleCon} ${classes.onboardingTitle}`}
                  component="h1"
                  style={{ color: communityTheme.mainColor, marginBottom: 0 }}
                >
                  Notification Policies
                </Typography>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}99`, marginBottom: 12 }}
              >
                Community: {selectedCommunity}
              </Typography>
            </>
          ) : view === "chatrooms" ? (
            <>
              <Box display="flex" alignItems="center" mb={1}>
                <IconButton
                  size="small"
                  onClick={() => setView("dashboard")}
                  style={{ marginRight: 8, color: communityTheme.mainColor }}
                >
                  <i className="material-icons">arrow_back</i>
                </IconButton>
                <Typography
                  className={`${classes.titleCon} ${classes.onboardingTitle}`}
                  component="h1"
                  style={{ color: communityTheme.mainColor, marginBottom: 0 }}
                >
                  Chat Rooms
                </Typography>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}99`, marginBottom: 12 }}
              >
                Community: {selectedCommunity}
              </Typography>
            </>
          ) : view === "emailSettings" ? (
            <>
              <Box display="flex" alignItems="center" mb={1}>
                <IconButton
                  size="small"
                  onClick={() => setView("dashboard")}
                  style={{ marginRight: 8, color: communityTheme.mainColor }}
                >
                  <i className="material-icons">arrow_back</i>
                </IconButton>
                <Typography
                  className={`${classes.titleCon} ${classes.onboardingTitle}`}
                  component="h1"
                  style={{ color: communityTheme.mainColor, marginBottom: 0 }}
                >
                  Email Settings
                </Typography>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}99`, marginBottom: 12 }}
              >
                Community: {selectedCommunity}
              </Typography>
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" mb={1}>
                <IconButton
                  size="small"
                  onClick={() => setView("dashboard")}
                  style={{ marginRight: 8, color: communityTheme.mainColor }}
                >
                  <i className="material-icons">arrow_back</i>
                </IconButton>
                <Typography
                  className={`${classes.titleCon} ${classes.onboardingTitle}`}
                  component="h1"
                  style={{ color: communityTheme.mainColor, marginBottom: 0 }}
                >
                  Manage Channels
                </Typography>
                {canManageCommunityWide && (
                  <ToggleButtonGroup
                    value={mode}
                    exclusive
                    size="small"
                    onChange={(_, val) => val && setMode(val)}
                    style={{
                      marginLeft: "auto",
                      backgroundColor: `${fgOnSecondary}0D`,
                      borderRadius: 24,
                      padding: 2,
                      border: `1px solid ${fgOnSecondary}22`,
                    }}
                  >
                    <ToggleButton
                      value="edit"
                      style={{
                        color: mode === "edit" ? communityTheme.secondaryColor : fgOnSecondary,
                        backgroundColor: mode === "edit" ? communityTheme.mainColor : "transparent",
                        borderRadius: 20,
                        padding: "2px 14px",
                        textTransform: "none",
                      }}
                    >
                      Edit
                    </ToggleButton>
                    <ToggleButton
                      value="order"
                      style={{
                        color: mode === "order" ? communityTheme.secondaryColor : fgOnSecondary,
                        backgroundColor: mode === "order" ? communityTheme.mainColor : "transparent",
                        borderRadius: 20,
                        padding: "2px 14px",
                        textTransform: "none",
                      }}
                    >
                      Order
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}99`, marginBottom: 12 }}
              >
                Community: {selectedCommunity}
              </Typography>
              {mode === "edit" && (
                <div
                  className={classes.searchInput}
                  style={{
                    border: `1px solid ${fgOnSecondary}33`,
                    backgroundColor: `${fgOnSecondary}0D`,
                  }}
                >
                  <InputBase
                    placeholder="Search"
                    fullWidth
                    startAdornment={<Search style={{ marginRight: 8, color: `${fgOnSecondary}99` }} />}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    inputProps={{ style: { color: fgOnSecondary } }}
                  />
                </div>
              )}
            </>
          )}
        </Box>

        {/* Content Area */}
        <Box
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "auto",
            padding: "0 16px 16px 16px",
          }}
        >
          {view === "dashboard" ? (
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 20,
              marginBottom: 16,
              maxWidth: 1400,
              margin: "0 auto",
            }}
          >
            {/* Channels Card */}
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => setView("channels")}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      forum
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Channels
                    </Typography>
                    <Typography
                      variant="h4"
                      style={{ color: communityTheme.mainColor, fontWeight: 700 }}
                    >
                      {channels.length}
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setView("channels");
                  }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, marginBottom: 16, lineHeight: 1.5 }}
              >
                Manage community channels, reorder, and configure settings
              </Typography>
              {canManageCommunityWide && (
                <Box display="flex" style={{ gap: 8 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddOpen(true);
                    }}
                    style={{
                      borderColor: communityTheme.mainColor,
                      color: communityTheme.mainColor,
                      textTransform: "none",
                      flex: 1,
                      fontWeight: 600,
                    }}
                  >
                    Add Channel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setView("channels");
                      setMode("order");
                    }}
                    style={{
                      backgroundColor: communityTheme.mainColor,
                      color: communityTheme.secondaryColor,
                      textTransform: "none",
                      flex: 1,
                      fontWeight: 600,
                    }}
                  >
                    Reorder
                  </Button>
                </Box>
              )}
            </MuiCard>

            {/* Members Card */}
            {canAccessRoles && (
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => history.push(`/admin/community/roles?community=${selectedCommunity}`)}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      people
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Members
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: `${fgOnSecondary}99` }}
                    >
                      Manage users
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, lineHeight: 1.5 }}
              >
                View and manage community members, roles, and permissions
              </Typography>
            </MuiCard>
            )}

            {/* Invitations Card */}
            {canManageCommunityWide && (
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => history.push(`/admin/invitations?community=${selectedCommunity}`)}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      mail_outline
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Invitations
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: `${fgOnSecondary}99` }}
                    >
                      Invite new members
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, lineHeight: 1.5 }}
              >
                Create and manage invitation codes for new members
              </Typography>
            </MuiCard>
            )}

            {/* Theme Card */}
            {canManageCommunityWide && (
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => {
                const pid = singleProgram?._id;
                if (!pid || !selectedCommunity) return;
                history.push(`/admin/programs/${pid}/communities/${selectedCommunity}/theme`);
              }}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      palette
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Theme
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: `${fgOnSecondary}99` }}
                    >
                      Customize appearance
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, marginBottom: 16, lineHeight: 1.5 }}
              >
                Customize colors, fonts, and branding for this community
              </Typography>
              <Box display="flex" style={{ gap: 8 }} alignItems="center">
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: communityTheme.mainColor,
                    border: `2px solid ${fgOnSecondary}33`,
                  }}
                />
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: communityTheme.secondaryColor,
                    border: `2px solid ${fgOnSecondary}33`,
                  }}
                />
                <Typography
                  variant="caption"
                  style={{ color: `${fgOnSecondary}99`, marginLeft: 4 }}
                >
                  Current colors
                </Typography>
              </Box>
            </MuiCard>
            )}

            {/* Notification Policies Card */}
            {canManageCommunityWide && (
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => setView("notificationPolicy")}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      notifications_active
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Notification Policies
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: `${fgOnSecondary}99` }}
                    >
                      Configure defaults
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, lineHeight: 1.5 }}
              >
                Set default notification preferences and policies for community members
              </Typography>
            </MuiCard>
            )}

            {/* Chat Rooms Card */}
            {canManageCommunityWide && (
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => setView("chatrooms")}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      forum
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Chat Rooms
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: `${fgOnSecondary}99` }}
                    >
                      Manage conversations
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, lineHeight: 1.5 }}
              >
                Manage chatrooms, group conversations, and direct messages for this community
              </Typography>
            </MuiCard>
            )}

            {/* Email Settings Card */}
            {canManageCommunityWide && (
            <MuiCard
              style={{
                backgroundColor: `${fgOnSecondary}0D`,
                border: `1px solid ${fgOnSecondary}22`,
                borderRadius: 12,
                padding: 24,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => setView("emailSettings")}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      backgroundColor: `${communityTheme.mainColor}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 16,
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ fontSize: 32, color: communityTheme.mainColor }}
                    >
                      email
                    </i>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      style={{ color: fgOnSecondary, fontWeight: 600, marginBottom: 4 }}
                    >
                      Email Settings
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: `${fgOnSecondary}99` }}
                    >
                      Configure provider
                    </Typography>
                  </Box>
                </Box>
                <i
                  className="material-icons"
                  style={{ fontSize: 20, color: `${fgOnSecondary}66` }}
                >
                  arrow_forward
                </i>
              </Box>
              <Typography
                variant="body2"
                style={{ color: `${fgOnSecondary}CC`, lineHeight: 1.5 }}
              >
                Set up a custom email provider (SendGrid or Brevo) for this program's outbound emails
              </Typography>
            </MuiCard>
            )}
          </Box>
          ) : view === "notificationPolicy" ? (
          // Notification Policy Editor View
          <Box>
            <NotificationPolicyEditor communityIdentifier={selectedCommunity} />
          </Box>
          ) : view === "chatrooms" ? (
          // Chat Rooms Manager View
          <Box>
            <AdminChatroomManager
              programId={singleProgram?._id || ""}
              communityIdentifier={selectedCommunity}
            />
          </Box>
          ) : view === "emailSettings" ? (
          // Email Settings View
          <Box>
            <AdminEmailSettings programId={singleProgram?._id || ""} />
          </Box>
          ) : (
          // Channels List View
          <List
            className={classes.communityListWrapper}
            style={{
              backgroundColor: "transparent",
              maxHeight: "none",
              overflowY: "visible",
            }}
          >
          {mode === "order" ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="channelList">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {orderedChannels.map((ch, idx) => (
                      <Draggable key={ch.channelIdentifier} draggableId={ch.channelIdentifier} index={idx}>
                        {(prov) => (
                          <ListItem
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            style={{
                              ...prov.draggableProps.style,
                              backgroundColor: `${fgOnSecondary}0D`,
                              borderRadius: 10,
                              marginBottom: 6,
                              border: `1px solid ${fgOnSecondary}22`,
                            }}
                          >
                            <IconButton {...prov.dragHandleProps} style={{ color: communityTheme.mainColor }}>
                              <DragIndicator />
                            </IconButton>
                            <ListItemText
                              primary={ch.channelName}
                              style={{ marginLeft: 20, color: fgOnSecondary }}
                            />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            filteredResults.map((ch, idx) => (
              <ListItem
                key={idx}
                style={{
                  backgroundColor: `${fgOnSecondary}0D`,
                  borderRadius: 10,
                  marginBottom: 6,
                  border: `1px solid ${fgOnSecondary}22`,
                }}
              >
                <i
                  className="material-icons icon"
                  style={{ fontSize: 25, padding: 4, marginRight: 12, color: communityTheme.mainColor }}
                >
                  {ch.channelPostingSettings?.defaultIcon || "note"}
                </i>
                <ListItemText
                  primary={ch.channelName}
                  style={{ marginLeft: 20, color: fgOnSecondary }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleEdit(ch)}
                  style={{
                    marginLeft: "auto",
                    marginRight: 8,
                    borderColor: communityTheme.mainColor,
                    color: communityTheme.mainColor,
                  }}
                  startIcon={<Edit />}
                >
                  EDIT
                </Button>
                {canManageCommunityWide && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChannel(ch)}
                    style={{ color: `${fgOnSecondary}CC` }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </ListItem>
            ))
          )}
          </List>
          )}
        </Box>

        {/* Footer */}
        {view === "channels" && mode === "order" && canManageCommunityWide ? (
          <Box
            textAlign="center"
            style={{
              flexShrink: 0,
              borderTop: `1px solid ${fgOnSecondary}22`,
              background: communityTheme.secondaryColor,
              padding: "12px 16px",
            }}
          >
            <Typography variant="body2" style={{ color: `${fgOnSecondary}CC`, marginBottom: 8 }}>
              Drag and drop to reorder your channels.
            </Typography>
            <Button
              variant="contained"
              onClick={handleSaveOrder}
              disabled={saving}
              style={{
                backgroundColor: communityTheme.mainColor,
                color: communityTheme.secondaryColor,
              }}
            >
              {saving ? "Saving…" : "Save Order"}
            </Button>
          </Box>
        ) : view === "channels" && canManageCommunityWide ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{
              flexShrink: 0,
              borderTop: `1px solid ${fgOnSecondary}22`,
              background: communityTheme.secondaryColor,
              padding: "12px 16px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setAddOpen(true)}
              style={{
                backgroundColor: communityTheme.mainColor,
                color: communityTheme.secondaryColor,
                textTransform: "none",
                borderRadius: 10,
                padding: "8px 16px",
                boxShadow: `0 4px 14px ${fgOnSecondary}22`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AddChannelIcon width={20} height={20} />
              Add Channel
            </Button>
          </Box>
        ) : null}
      </MuiCard>
    </div>
  );
}
